import React, { FC, useEffect, useState, useMemo, Fragment } from 'react'
import { observer } from 'mobx-react'
import { menuMobx, cartMobx, restaurantConfig, storeMobx } from 'restaurant'
import { advertMobx, boughtProductMobx, buyTogetherMobx, decorationMobx, themeMobx } from '@mobxStore'
import productStyleController from '@ctrls/productStyleController'
import { deepClone } from '@dt/fe-utils'
import { cloneDeep, countBy, filter, findIndex, forEach, groupBy, keyBy, map } from '@dt/fe-utils/dist/lodash'
import { Empty, SoldoutFoldBar, WaterfallSkeleton } from '@cps'
import { WaterfallAdvertItem } from '../../../'
import noMealImg from '@img/no-meal.png'
import ProductItem from '@pages/menu/components/ProductItem'
import BoughtProducts from '../../BoughtProducts'
import BTAndCouponBar from '../../BTAndCouponBar'
import CategoryItem from '../CategoryItem'
import './index.scss'

interface IProps {
  lazy?: boolean;
  products?: IViewProduct[];
  selectCategoryNo?: string;
}

// 存储计算结果
let lineToResult = {}
// 存储零时分组no
let saveCategoryNo = ''
/** 菜单容器宽度 去掉左侧导航 */
const wrapWidth = window.innerWidth - 74
/** 广告类型 */
const advertType = 'waterfall'
/** 瀑布流计算定时器 */
let waterfallTimer
/** 瀑布流计算超过5次停掉 */
let calcStep = 0

const WaterfallTemplate: FC<IProps> = ({ lazy = true, products: _products }) => {
  const isBuyTogether = !!Config.buyTogetherId
  const [products, setProducts] = useState([])
  const [height, setHeight] = useState(420)
  const [waterfallAdvertMap, setWaterfallAdvertMap] = useState({})
  const [productStyleMap, setProductStyleMap] = useState({} as {
    [no: string]: {
      left: number;
      top: number;
    };
  })
  const displayMode = 'waterfallCategory'
  const { advertInfo, availableAdsMap } = advertMobx
  const advert = advertInfo[advertType] || null
  const advertAdList = availableAdsMap[advertType] || []
  const { categories } = menuMobx

  /**
   * 计算分类下有多少餐品
   * count 当前分类包含自己之前分类的餐品数量
   * length 当前分类的下标
   * soldoutCnt 售罄餐品数量
   * productCount 当前分类展示的餐品数量
   * producNos 当前展示餐品的productNo
   */
  const getCateLens = useMemo(() => {
    let saveCount = 0
    return map(groupBy(_products, 'categoryNo'), (values, no) => {
      // 售罄餐品数量
      const soldoutCnt = values.filter(item => item.disable).length || 0
      // 渲染餐品数量，餐品折叠只展示未售罄，未折叠展示全部餐品
      const doCnt = menuMobx.foldSoldoutMap?.[no] === true ? values.length : values.length - soldoutCnt
      saveCount += doCnt
      // 当前分类的下标
      const currentIndex = findIndex(categories, category => category.no === no)
      return {
        categoryNo: no,
        currentIndex,
        /** 当前到之前分类的餐品数量 */
        count: saveCount,
        /** 当前分类展示的餐品数量 */
        productCount: doCnt,
        soldoutCnt: soldoutCnt,
        producNos: map(values, item => item.no)
      }
    })
  }, [_products, menuMobx.foldSoldoutMap])

  /** categoryNo为key */
  const getCateLenMap = useMemo(() => {
    return keyBy(getCateLens, 'categoryNo')
  }, [products, menuMobx.foldSoldoutMap])
  const hashProduct = useMemo(() => {
    const channelEatType = storeMobx.info.storeId + restaurantConfig.mpc.channel + restaurantConfig.mpc.eatType
    return channelEatType + products.map(p => p.no).join('')
  }, [products])
  /** 瀑布流插入需要计算的数据 */
  const calcSoltItem = () => {
    let calcItems = deepClone(products)

    // 插入数组后，index会偏移
    let offsetSoldoutBarIndex = 0
    forEach(getCateLens, (item) => {
      const index = item.count || 0
      calcItems.splice(index + offsetSoldoutBarIndex, 0, { no: `soldout_bar_${item.categoryNo}` })
      offsetSoldoutBarIndex++
    })

    // 插入数组后，index会偏移
    let offsetAdvertIndex = 0
    Object.keys(waterfallAdvertMap).forEach((key) => {
      waterfallAdvertMap?.[key]?.forEach((item, j) => {
        const i = Number(key)
        const categoryNo = calcItems[i + offsetAdvertIndex]?.categoryNo || ''
        const preCount = getCateLenMap?.[categoryNo]?.currentIndex || 0
        calcItems.splice(i + offsetAdvertIndex + preCount, 0, { no: `advert_${key}_${j}` })
        offsetAdvertIndex++
      })
    })
    let items = [
      { no: 'bought' },
      { no: 'coupon' },
      ...calcItems
    ]
    return items as IViewProduct[]
  }
  const calcWaterfall = () => {
    clearTimeout(waterfallTimer)
    if (displayMode !== restaurantConfig.displayMode) return
    const productsSolts = calcSoltItem()
    const payload = productStyleController.waterfall(productsSolts, lazy, 'no', '', 'mini')
    if (payload) {
      const { styleMap, height } = payload
      lineToResult[hashProduct] = payload
      setProductStyleMap(styleMap)
      setHeight(height)
    } else {
      if (calcStep <= 5) {
        setProductStyleMap({})
        waterfallTimer = setTimeout(() => {
          calcWaterfall()
        }, 300)
        ++calcStep
      }
    }
  }
  /** 瀑布流广告 */
  const setMenuWaterfallAdvert = () => {
    const adLen = advertAdList?.length
    const prodLen = _products?.length
    const queueType = advert?.extInfo?.queueType || 'auto'
    const queueArr = advert?.extInfo?.queueData?.[queueType]
    const advertKeys = (queueType === 'auto'
      ? (queueArr || []).slice(0, adLen)
      : advertAdList?.map((item, index) => {
        return (item.wfQueue >= 0 && item.wfQueue) || (queueArr?.[index] >= 0 && queueArr?.[index]) || 0
      }) || [])
    const advertMap: { [key: string]: any[] } = {}
    forEach(advertKeys, (item: number, index: number) => {
      if (item >= 0) {
        let key = String(item)
        // 当如果添加key超过商品总数，就放到最后
        if (item >= prodLen) {
          key = String(prodLen)
        }
        advertMap[key] = [
          ...advertMap[key] || [],
          advertAdList[index]
        ]
      }
    })
    setWaterfallAdvertMap(advertMap)
  }

  /** 计算售罄条,分组锚点列表 */
  const SoldoutBarList = () => {
    return (
      <>
        {
          map(getCateLenMap, item => {
            const categoryNo = item.categoryNo
            const showSoldout = menuMobx.openFoldSoldout === 1 && item.soldoutCnt > 0
            const isEmpty = !item.productCount
            const categoryStyleKey = isEmpty ? `soldout_bar_${categoryNo}` : item.producNos?.[0] + categoryNo
            return (
              <Fragment key={categoryNo}>
                <CategoryItem
                  categoryNo={categoryNo}
                  style={productStyleMap[categoryStyleKey] || { visibility: 'hidden' }}
                />
                {showSoldout ? (
                  <SoldoutFoldBar
                    cnt={getCateLenMap?.[categoryNo]?.soldoutCnt}
                    className='product-item mini'
                    show={menuMobx.foldSoldoutMap?.[categoryNo] === true}
                    no={categoryNo}
                    from='menu'
                    style={productStyleMap[`soldout_bar_${categoryNo}`] || { visibility: 'hidden' }}
                  />
                ) : null}
              </Fragment>
            )
          })
        }
      </>
    )
  }

  useEffect(() => {
    saveCategoryNo = ''
    setMenuWaterfallAdvert()
    if (products.length && hashProduct) {
      if (lineToResult[hashProduct]) {
        setProductStyleMap(lineToResult[hashProduct].styleMap)
        setHeight(lineToResult[hashProduct].height)
      } else {
        setTimeout(() => {
          calcWaterfall()
        }, 300)
      }
    }
  }, [products, hashProduct, advertAdList, boughtProductMobx.boughtViewProduct.length, menuMobx.foldSoldoutMap])

  useEffect(() => {
    setMenuWaterfallAdvert()
    setProducts(productStyleController.setProductImgHeight(filter(deepClone(_products), (item) => {
      // 未售罄或者 售罄并且开启折叠售罄并且展开
      return !item.disable || !menuMobx.openFoldSoldout || menuMobx.foldSoldoutMap?.[item.categoryNo] === true
    })))
  }, [_products, menuMobx.foldSoldoutMap])

  useEffect(() => {
    if (restaurantConfig.displayMode !== 'waterfallCategory') {
      clearTimeout(waterfallTimer)
    }
    return () => {
      productStyleController.clearWaterfallData()
      clearTimeout(waterfallTimer)
    }
  }, [restaurantConfig.displayMode])

  useEffect(() => {
    lineToResult = {}
    productStyleController.clearWaterfallData()
    calcStep = 0
  }, [])

  return (
    <>
      {
        menuMobx.isLoading ? <WaterfallSkeleton /> : products.length ? ( // 且有餐品
          <div className='waterfall-style product-list-content'>
            <div className='waterfall-template' style={{ height }}>
              <BoughtProducts style={productStyleMap.bought || { visibility: 'hidden' }} />
              <BTAndCouponBar style={productStyleMap.coupon || { visibility: 'hidden' }} />
              {!!products.length && waterfallAdvertMap?.[0]?.map((item, j) => (
                <WaterfallAdvertItem
                  wrapWidth={wrapWidth}
                  key={`advert_0_${j}`}
                  data={item}
                  style={productStyleMap[`advert_0_${j}`]}
                  type='waterfall'
                  id={`advert_0_${j}`}
                />
              ))}
              {
                products.map((product, i) => {
                  const categoryNo = product.categoryNo || ''
                  return (
                    <Fragment key={product.no + categoryNo}>
                      <ProductItem
                        wrapWidth={wrapWidth}
                        mealDecorates={decorationMobx.mealDecorates || {} as TMealCommonDecorates}
                        style={productStyleMap[product.no + categoryNo] || { visibility: 'hidden' }}
                        key={`${product.no}_${categoryNo}`}
                        product={product}
                        count={isBuyTogether ? buyTogetherMobx.productNoToCntMapForMenu?.[product.no] || 0 : cartMobx.cartResult?.productNoToCntMap?.[product.no] || 0}
                        lazy={lazy}
                        size='mini'
                        backgroundMode={themeMobx.data.backgroundMode}
                        imgLoaded={menuMobx.imgLoadingMap[product.no + categoryNo]}
                      />
                      {!!products.length && waterfallAdvertMap?.[i + 1]?.map((item, j) => (
                        <WaterfallAdvertItem
                          wrapWidth={wrapWidth}
                          key={`advert_${i + 1}_${j}`}
                          data={item}
                          style={productStyleMap[`advert_${i + 1}_${j}`] || { visibility: 'hidden' }}
                          type='waterfall'
                          id={`advert_${i + 1}_${j}`}
                        />))}
                    </Fragment>
                  )
                })
              }
              <SoldoutBarList />
            </div>
          </div>
        ) : ( // 无餐品
          <Empty
            msg={themeMobx?.data?.abnormalStateSetting?.[0]?.subName || ''}
            iconType='dicon'
            iconName={themeMobx?.data?.abnormalStateSetting[0]?.icon}
            picUrl={themeMobx?.data?.abnormalStateSetting[0]?.pic || noMealImg}
            height='55vh'
          />
        )
      }
    </>
  )
}
export default observer(WaterfallTemplate)
